import { cva } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { cn } from '../../lib/cn'
import { focusRing } from '../../lib/focus-ring'
import { Avatar, type AvatarTintBySize } from '../../atoms/avatar/avatar'
import { Icon } from '../../atoms/icon/icon'
import { Search, type SearchProps } from '../../molecules/search/search'

/**
 * The Heading (210:16734): the app's top bar. The "ATLAS" wordmark is Roboto Bold
 * 24.084 on the left; on the right, 32px apart, the Default Search and a group of
 * the 32px Notification bell and the signed-in user (a 36px Avatar and their name
 * in Body SemiBold), 12px apart.
 *
 * The name binds the Components board's second "Dark" (#080712), `dark-ink`.
 *
 * Keyboard focus takes the kit's accent outline, which is not drawn in the Figma.
 */
export const headingVariants = cva(
	'flex w-full items-center justify-between bg-atlas-light px-atlas-heading-inset-x py-3 font-atlas-sans',
)

/** The signed-in user shown on the right of the bar. */
export interface HeadingUser {
	name: string
	/** The initials drawn in the avatar, e.g. "HF". */
	initials: string
	tint?: AvatarTintBySize['md']
}

export interface HeadingProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
	user: HeadingUser
	/** The product name on the left. */
	wordmark?: string
	/** Passed to the Search field: `onSubmit`, `onChange`, `value`, `aria-label`. */
	searchProps?: Omit<SearchProps, 'type'>
	/** Shows the bell as Notification = True, the unread variant (481:62671). */
	hasUnreadNotifications?: boolean
	/** Called when the bell is pressed. */
	onNotificationsPress?: () => void
	notificationsLabel?: string
}

/**
 * The app header as a `header` landmark. Presentational: the screen owns the
 * search query, the notification count and the user; the bell reports presses.
 */
export function Heading({
	user,
	wordmark = 'ATLAS',
	searchProps,
	hasUnreadNotifications = false,
	onNotificationsPress,
	notificationsLabel = 'Notifications',
	className,
	...props
}: HeadingProps) {
	return (
		<header {...props} className={cn(headingVariants(), className)}>
			<p className="min-w-0 flex-1 font-atlas-roboto text-atlas-wordmark text-atlas-dark">{wordmark}</p>
			<div className="flex shrink-0 items-center gap-8">
				<Search aria-label="Search" {...searchProps} type="default" className={cn('w-100', searchProps?.className)} />
				<div className="flex items-center gap-3">
					<AriaButton
						aria-label={notificationsLabel}
						onPress={onNotificationsPress}
						className={cn('inline-flex shrink-0 cursor-pointer rounded-atlas-full', focusRing)}
					>
						<Icon name={hasUnreadNotifications ? 'notification-unread' : 'notification'} />
					</AriaButton>
					<div className="flex items-center gap-2.5">
						<Avatar initials={user.initials} tint={user.tint} aria-hidden="true" />
						<span className="whitespace-nowrap text-atlas-body-semibold text-atlas-dark-ink">{user.name}</span>
					</div>
				</div>
			</div>
		</header>
	)
}
